function ageCalculator() {
    return {
        mode: 'single', 
        hasResult: false,
        error: '',
        
        dob: '',
        tob: '',
        refDate: new Date().toISOString().split('T')[0],
        dob2: '',

        result: { years: 0, months: 0, days: 0 },
        lifeStats: { totalMonths: 0, totalWeeks: 0, totalDays: 0, totalHours: 0, totalMinutes: 0 },
        liveSeconds: 0,
        bornOnDay: '',
        zodiac: { name: '', emoji: '' },
        nextBirthdayDate: '',
        nextBirthdayText: '',
        daysToNext: 0,
        milestones: [],
        eligibility: [],

        age1: { display: '', totalDays: 0 },
        age2: { display: '', totalDays: 0 },
        differenceSummary: '',
        whoIsOlderText: '',

        init() {
            const params = new URLSearchParams(window.location.search);
            if (params.has('dob')) this.dob = params.get('dob');
            if (params.has('tob')) this.tob = params.get('tob');
            if (params.has('ref')) this.refDate = params.get('ref');
            if (params.has('dob2')) {
                this.dob2 = params.get('dob2');
                this.mode = 'comparison';
            }
            if (this.dob) this.calculate();

            setInterval(() => {
                if (this.hasResult && this.mode === 'single') this.updateLiveSeconds();
            }, 1000);
        },

        copySummary() {
            let text = `Age Report\n----------\n`;
            if (this.mode === 'single') {
                text += `Age: ${this.result.years} Years, ${this.result.months} Months, ${this.result.days} Days\n`;
                text += `Born: ${this.dob} (${this.bornOnDay})\n`;
                text += `Total Days: ${this.format(this.lifeStats.totalDays)}\n`;
                text += `Zodiac: ${this.zodiac.name}\n`;
            } else {
                text += `Person 1: ${this.age1.display}\n`;
                text += `Person 2: ${this.age2.display}\n`;
                text += `Difference: ${this.differenceSummary}\n`;
                text += `Who is older: ${this.whoIsOlderText}\n`;
            }
            navigator.clipboard.writeText(text);
            alert("Summary copied to clipboard!");
        },

        format(num) {
            return new Intl.NumberFormat().format(num);
        },

        reset() {
            this.dob = ''; this.tob = ''; this.dob2 = '';
            this.refDate = new Date().toISOString().split('T')[0];
            this.hasResult = false;
            this.error = '';
        },

        calculate() {
            if (this.mode === 'single' && !this.dob) return;
            if (this.mode === 'comparison' && (!this.dob || !this.dob2)) return;

            try {
                if (this.mode === 'single') {
                    this.runSingleCalculation();
                } else {
                    this.runComparisonCalculation();
                }
                this.hasResult = true;
                this.error = "";
            } catch (e) {
                this.error = "An error occurred with the input dates.";
                this.hasResult = false;
            }
        },

        getDetailedAge(born, ref) {
            let years = ref.getFullYear() - born.getFullYear();
            let months = ref.getMonth() - born.getMonth();
            let days = ref.getDate() - born.getDate();

            if (days < 0) {
                const prevMonth = new Date(ref.getFullYear(), ref.getMonth(), 0);
                days += prevMonth.getDate();
                months -= 1;
            }
            if (months < 0) {
                months += 12;
                years -= 1;
            }
            return { years, months, days };
        },

        runSingleCalculation() {
            const bornString = this.dob + (this.tob ? `T${this.tob}` : 'T00:00:00');
            const born = new Date(bornString);
            const ref = new Date(this.refDate + 'T23:59:59');
            
            if (born > ref) throw new Error("Past");

            const res = this.getDetailedAge(born, ref);
            this.result = res;

            const diffMs = ref - born;
            const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            this.lifeStats = {
                totalMonths: (res.years * 12) + res.months,
                totalWeeks: Math.floor(totalDays / 7),
                totalDays: totalDays,
                totalHours: totalDays * 24,
                totalMinutes: totalDays * 24 * 60
            };
            this.updateLiveSeconds();

            this.eligibility = [
                { label: 'School (5+)', passed: res.years >= 5 },
                { label: 'Teen (13+)', passed: res.years >= 13 },
                { label: 'Adult (18+)', passed: res.years >= 18 },
                { label: 'Drink (21+)', passed: res.years >= 21 },
                { label: 'Senior (60+)', passed: res.years >= 60 }
            ];

            this.bornOnDay = born.toLocaleDateString(undefined, { weekday: 'long' });
            this.zodiac = this.getZodiac(born.getMonth() + 1, born.getDate());

            let next = new Date(ref.getFullYear(), born.getMonth(), born.getDate());
            if (next <= ref) next.setFullYear(ref.getFullYear() + 1);
            this.nextBirthdayDate = next.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
            this.nextBirthdayText = "Next Birthday";
            this.daysToNext = Math.ceil((next - ref) / (1000 * 60 * 60 * 24));

            this.calculateMilestones(born, ref);
        },

        updateLiveSeconds() {
            if (!this.dob) return;
            const born = new Date(this.dob + (this.tob ? `T${this.tob}` : 'T00:00:00'));
            this.liveSeconds = Math.floor((new Date() - born) / 1000);
        },

        runComparisonCalculation() {
            const p1 = new Date(this.dob + 'T00:00:00');
            const p2 = new Date(this.dob2 + 'T00:00:00');
            const ref = new Date();

            const res1 = this.getDetailedAge(p1, ref);
            const res2 = this.getDetailedAge(p2, ref);
            
            this.age1 = { 
                display: `${res1.years}y ${res1.months}m ${res1.days}d`,
                totalDays: Math.floor((ref - p1)/(1000*60*60*24))
            };
            this.age2 = { 
                display: `${res2.years}y ${res2.months}m ${res2.days}d`,
                totalDays: Math.floor((ref - p2)/(1000*60*60*24))
            };

            const start = p1 < p2 ? p1 : p2;
            const end = p1 < p2 ? p2 : p1;
            const diff = this.getDetailedAge(start, end);

            this.differenceSummary = `${diff.years}y ${diff.months}m ${diff.days}d`;
            
            if (p1.getTime() === p2.getTime()) {
                this.whoIsOlderText = "Both people are exactly the same age!";
            } else {
                const older = p1 < p2 ? "Person 1" : "Person 2";
                this.whoIsOlderText = `${older} is older than Person ${older === 'Person 1' ? '2' : '1'}`;
            }
        },

        calculateMilestones(born, ref) {
            const list = [
                { label: "1,000 Days Alive", days: 1000 },
                { label: "5,000 Days Alive", days: 5000 },
                { label: "10,000 Days Alive", days: 10000 },
                { label: "20,000 Days Alive", days: 20000 },
                { label: "1,000,000,000 Seconds", seconds: 1000000000 },
                { label: "2,000,000,000 Seconds", seconds: 2000000000 },
                { label: "50th Anniversary", years: 50 }
            ];

            this.milestones = list.map(m => {
                let date;
                if (m.days) date = new Date(born.getTime() + m.days * 24 * 60 * 60 * 1000);
                else if (m.seconds) date = new Date(born.getTime() + m.seconds * 1000);
                else if (m.years) date = new Date(born.getFullYear() + m.years, born.getMonth(), born.getDate());

                return {
                    label: m.label,
                    date: date.toLocaleDateString(),
                    passed: date < ref
                };
            });
        },

        getZodiac(month, day) {
            const signs = [
                { name: "Capricorn", emoji: "♑", m: 1, d: 20 },
                { name: "Aquarius", emoji: "♒", m: 2, d: 19 },
                { name: "Pisces", emoji: "♓", m: 3, d: 20 },
                { name: "Aries", emoji: "♈", m: 4, d: 20 },
                { name: "Taurus", emoji: "♉", m: 5, d: 21 },
                { name: "Gemini", emoji: "♊", m: 6, d: 21 },
                { name: "Cancer", emoji: "♋", m: 7, d: 23 },
                { name: "Leo", emoji: "♌", m: 8, d: 23 },
                { name: "Virgo", emoji: "♍", m: 9, d: 23 },
                { name: "Libra", emoji: "♎", m: 10, d: 23 },
                { name: "Scorpio", emoji: "♏", m: 11, d: 22 },
                { name: "Sagittarius", emoji: "♐", m: 12, d: 22 },
                { name: "Capricorn", emoji: "♑", m: 13, d: 20 }
            ];
            const sign = signs.find((s, i) => (month === s.m && day <= s.d) || (month === s.m - 1 && day > (signs[i-1] ? signs[i-1].d : 31)));
            return sign || signs[0];
        }
    };
}

if (typeof window.Alpine !== 'undefined') {
    Alpine.data('ageCalculator', ageCalculator);
} else {
    document.addEventListener('alpine:init', () => {
        Alpine.data('ageCalculator', ageCalculator);
    });
}
